import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, Brain, User, Building2, Shield, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/navbar';
import LanguageSelector from './LanguageSelector';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const isEmployerRoute = location.pathname.startsWith('/employer');
  const isDCVEERoute = location.pathname.startsWith('/dcvee');

  return (
    <nav className="bg-background shadow-sm border-b border-border backdrop-blur-sm bg-background/90 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <Brain className="h-8 w-8 text-accent" />
                <span className="ml-2 text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">Cortex AI</span>
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link 
                to="/" 
                className={`${
                  location.pathname === '/' 
                    ? 'border-accent text-foreground' 
                    : 'border-transparent text-foreground/70 hover:border-accent hover:text-foreground'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                {t.menu.home}
              </Link>
              <Link 
                to="/precios" 
                className={`${
                  location.pathname === '/precios' 
                    ? 'border-accent text-foreground' 
                    : 'border-transparent text-foreground/70 hover:border-accent hover:text-foreground'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                {t.menu.pricing}
              </Link>
              <Link 
                to="/nosotros" 
                className={`${
                  location.pathname === '/nosotros' 
                    ? 'border-accent text-foreground' 
                    : 'border-transparent text-foreground/70 hover:border-accent hover:text-foreground'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                {t.menu.about}
              </Link>
              <Link 
                to="/formacion" 
                className={`${
                  location.pathname === '/formacion' 
                    ? 'border-accent text-foreground' 
                    : 'border-transparent text-foreground/70 hover:border-accent hover:text-foreground'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                {t.menu.training}
              </Link>
              {user && !isEmployerRoute && !isDCVEERoute && (
                <>
                  <Link 
                    to="/dashboard" 
                    className={`${
                      location.pathname === '/dashboard' || location.pathname.startsWith('/simulacion') 
                        ? 'border-accent text-foreground' 
                        : 'border-transparent text-foreground/70 hover:border-accent hover:text-foreground'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    {t.menu.dashboard}
                  </Link>
                  <Link 
                    to="/ruta-al-exito" 
                    className={`${
                      location.pathname === '/ruta-al-exito' 
                        ? 'border-accent text-foreground' 
                        : 'border-transparent text-foreground/70 hover:border-accent hover:text-foreground'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                  >
                    {t.menu.successPath}
                  </Link>
                </>
              )}
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className={`${
                    location.pathname === '/admin' 
                      ? 'border-accent text-foreground' 
                      : 'border-transparent text-foreground/70 hover:border-accent hover:text-foreground'
                  } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                >
                  <Shield className="h-4 w-4 mr-1" />
                  {t.menu.admin}
                </Link>
              )}
            </div>
          </div>
          <div className="hidden sm:ml-6 sm:flex sm:items-center space-x-4">
            <LanguageSelector />
            {user ? (
              <div className="flex items-center space-x-4">
                <Link 
                  to="/perfil" 
                  className="flex items-center text-foreground/70 hover:text-foreground"
                >
                  <User className="h-5 w-5 mr-1" />
                  <span className="text-sm">
                    {user.user_metadata?.name || t.menu.profile}
                  </span>
                </Link>
                <button
                  onClick={handleSignOut}
                  className="flex items-center text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium"
                >
                  <LogOut className="h-4 w-4 mr-1" />
                  {t.auth.logout}
                </button>
              </div>
            ) : (
              <>
                {!isEmployerRoute && !isDCVEERoute ? (
                  <div className="flex items-center space-x-4">
                    <Link 
                      to="/employer/login" 
                      className="text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium flex items-center"
                    >
                      <Building2 className="h-4 w-4 mr-1" />
                      {t.employer.portal}
                    </Link>
                    <Link 
                      to="/dcvee" 
                      className="text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium flex items-center"
                    >
                      <Building2 className="h-4 w-4 mr-1" />
                      DCVEE
                    </Link>
                    <Link 
                      to="/login" 
                      className="text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium"
                    >
                      {t.auth.login}
                    </Link>
                    <Link 
                      to="/registro" 
                      className="ml-4 px-4 py-2 rounded-md text-sm font-medium text-white bg-accent hover:bg-accent/90 shadow-sm hover:shadow transition-all"
                    >
                      {t.auth.register}
                    </Link>
                  </div>
                ) : (
                  <div className="flex items-center space-x-4">
                    <Link 
                      to="/login" 
                      className="text-foreground/70 hover:text-foreground px-3 py-2 rounded-md text-sm font-medium flex items-center"
                    >
                      <User className="h-4 w-4 mr-1" />
                      {t.employer.userPortal}
                    </Link>
                    <Link 
                      to={isDCVEERoute ? "/dcvee/login" : "/employer/login"}
                      className="ml-4 px-4 py-2 rounded-md text-sm font-medium text-white bg-accent hover:bg-accent/90 shadow-sm hover:shadow transition-all"
                    >
                      {t.employer.access}
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-foreground/70 hover:text-foreground hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="sm:hidden bg-background">
          <div className="pt-2 pb-3 space-y-1">
            <Link 
              to="/" 
              className={`${
                location.pathname === '/' 
                  ? 'bg-accent/10 border-accent text-accent' 
                  : 'border-transparent text-foreground/70 hover:bg-secondary hover:border-border hover:text-foreground'
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t.menu.home}
            </Link>
            <Link 
              to="/precios" 
              className={`${
                location.pathname === '/precios' 
                  ? 'bg-accent/10 border-accent text-accent' 
                  : 'border-transparent text-foreground/70 hover:bg-secondary hover:border-border hover:text-foreground'
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t.menu.pricing}
            </Link>
            <Link 
              to="/nosotros" 
              className={`${
                location.pathname === '/nosotros' 
                  ? 'bg-accent/10 border-accent text-accent' 
                  : 'border-transparent text-foreground/70 hover:bg-secondary hover:border-border hover:text-foreground'
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t.menu.about}
            </Link>
            <Link 
              to="/formacion" 
              className={`${
                location.pathname === '/formacion' 
                  ? 'bg-accent/10 border-accent text-accent' 
                  : 'border-transparent text-foreground/70 hover:bg-secondary hover:border-border hover:text-foreground'
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t.menu.training}
            </Link>
            {user && !isEmployerRoute && !isDCVEERoute && (
              <>
                <Link 
                  to="/dashboard" 
                  className={`${
                    location.pathname === '/dashboard' || location.pathname.startsWith('/simulacion')
                      ? 'bg-accent/10 border-accent text-accent' 
                      : 'border-transparent text-foreground/70 hover:bg-secondary hover:border-border hover:text-foreground'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t.menu.dashboard}
                </Link>
                <Link 
                  to="/ruta-al-exito" 
                  className={`${
                    location.pathname === '/ruta-al-exito'
                      ? 'bg-accent/10 border-accent text-accent' 
                      : 'border-transparent text-foreground/70 hover:bg-secondary hover:border-border hover:text-foreground'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t.menu.successPath}
                </Link>
              </>
            )}
            {isAdmin && (
              <Link 
                to="/admin" 
                className={`${
                  location.pathname === '/admin'
                    ? 'bg-accent/10 border-accent text-accent' 
                    : 'border-transparent text-foreground/70 hover:bg-secondary hover:border-border hover:text-foreground'
                } block pl-3 pr-4 py-2 border-l-4 text-base font-medium flex items-center`}
                onClick={() => setIsMenuOpen(false)}
              >
                <Shield className="h-4 w-4 mr-2" />
                {t.menu.admin}
              </Link>
            )}
          </div>
          <div className="pt-4 pb-3 border-t border-border">
            <div className="px-4 py-2">
              <LanguageSelector />
            </div>
            {user ? (
              <div className="flex items-center px-4">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center text-white font-medium">
                    {(user.user_metadata?.name || 'U').charAt(0)}
                  </div>
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-foreground">
                    {user.user_metadata?.name || t.menu.profile}
                  </div>
                  <div className="text-sm font-medium text-foreground/60">
                    {user.email}
                  </div>
                </div>
                <button
                  onClick={handleSignOut}
                  className="ml-auto flex-shrink-0 p-1 rounded-full text-foreground/60 hover:text-foreground"
                >
                  <LogOut className="h-6 w-6" />
                </button>
              </div>
            ) : (
              <div>
                <div className="flex items-center px-4">
                  {!isEmployerRoute && !isDCVEERoute ? (
                    <>
                      <Link 
                        to="/employer/login" 
                        className="block px-4 py-2 text-base font-medium text-foreground/70 hover:text-foreground hover:bg-secondary flex items-center"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        {t.employer.portal}
                      </Link>
                      <Link 
                        to="/dcvee" 
                        className="block px-4 py-2 text-base font-medium text-foreground/70 hover:text-foreground hover:bg-secondary flex items-center"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        DCVEE
                      </Link>
                      <Link 
                        to="/login" 
                        className="block px-4 py-2 text-base font-medium text-foreground/70 hover:text-foreground hover:bg-secondary"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {t.auth.login}
                      </Link>
                    </>
                  ) : (
                    <Link 
                      to="/login" 
                      className="block px-4 py-2 text-base font-medium text-foreground/70 hover:text-foreground hover:bg-secondary flex items-center"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      {t.employer.userPortal}
                    </Link>
                  )}
                </div>
                <div className="mt-3 space-y-1">
                  {!isEmployerRoute && !isDCVEERoute && (
                    <Link 
                      to="/registro" 
                      className="block px-4 py-2 text-base font-medium text-foreground/70 hover:text-foreground hover:bg-secondary"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t.auth.register}
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;